
import React, { useState, useRef, Suspense, useEffect } from "react";
import { productsUrl } from "../../api";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import { Canvas, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls, Stage, useGLTF, Html } from '@react-three/drei';
import { STLLoader } from 'three/examples/jsm/loaders/STLLoader';
import * as THREE from 'three';

// Component to handle model loading and rendering
// Sub-component for STL files
const STLModel = ({ url }) => {
    const geometry = useLoader(STLLoader, url);
    return (
        <mesh geometry={geometry}>
            <meshStandardMaterial color="gray" />
        </mesh>
    );
};

// Sub-component for GLB/GLTF files
const GLTFModel = ({ url }) => {
    const { scene } = useGLTF(url);
    return <primitive object={scene} />;
};

// Main Model Dispatcher
const Model = ({ url, fileName, onLoaded }) => {
    // Check extension from fileName if available, otherwise fallback to url
    const nameToCheck = fileName || url;
    const isStl = nameToCheck.toLowerCase().endsWith('.stl');
    const { gl } = useThree();

    useEffect(() => {
        if (onLoaded) onLoaded(gl);
    }, [onLoaded, gl]);

    if (isStl) return <STLModel url={url} />;
    return <GLTFModel url={url} />;
};

const EditProduct = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  
  // State for form inputs
  const [bookState, setBookState] = useState({
      title: "",
      author: "",
      currentPrice: "",
      previousPrice: "",
      description: "",
      images: [] // Existing gallery images
  });
  
  const [coverImage, setCoverImage] = useState(null);
  const [galleryFiles, setGalleryFiles] = useState([]); // New files to upload
  const [modelFile, setModelFile] = useState(null);
  
  // For previews
  const [existingCoverUrl, setExistingCoverUrl] = useState("");
  const [modelPreviewUrl, setModelPreviewUrl] = useState(null);
  const [modelFileName, setModelFileName] = useState("");
  const [generatedThumbnail, setGeneratedThumbnail] = useState(null);

  // Fetch product data
  useEffect(() => {
      fetch(`${productsUrl}${id}`)
          .then(res => res.json())
          .then(response => {
              const data = response.data;
              setBookState({
                  title: data.title || "",
                  author: data.additionalDetails?.author || "",
                  currentPrice: data.currentPrice || "",
                  previousPrice: data.previousPrice || "",
                  description: data.description || "",
                  images: data.images || []
              });
              
              setExistingCoverUrl(data.coverImage);
              setFetching(false);
          })
          .catch(err => {
              console.error(err);
              toast.error("Failed to fetch product details");
              setFetching(false);
          });
  }, [id]);

  // Handle Input Change
  const handleChange = (e) => {
      const { name, value } = e.target;
      setBookState(prev => ({ ...prev, [name]: value }));
  };
  
  const handleRemoveImage = (index) => {
      setBookState(prev => ({
          ...prev,
          images: prev.images.filter((_, i) => i !== index)
      }));
  };

  // Handle Model Selection... (keep existing)
  const handleModelSelect = (e) => {
      const file = e.target.files[0];
      if (file) {
          setModelFile(file);
          setModelFileName(file.name);
          const url = URL.createObjectURL(file);
          setModelPreviewUrl(url);
      }
  };

  const handleCaptureThumbnail = () => {
    const canvas = document.querySelector('canvas');
    if (canvas) {
        const dataUrl = canvas.toDataURL('image/png');
        setGeneratedThumbnail(dataUrl);
        fetch(dataUrl)
            .then(res => res.blob())
            .then(blob => {
                const file = new File([blob], "thumbnail.png", { type: "image/png" });
                setCoverImage(file);
                toast.success("Thumbnail generated and set as Cover Image!");
            });
    }
  };

  const uploadFile = async (file) => {
    const formData = new FormData();
    formData.append('modelFile', file);
    const res = await fetch('https://backend.cdp3d.com/api/upload-model', {
        method: 'POST',
        body: formData
    });
    const data = await res.json();
    return data.filePath;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
        let coverImageUrl = existingCoverUrl;
        let modelFileUrl = null; 
        let newGalleryUrls = [];

        if (coverImage) {
            coverImageUrl = await uploadFile(coverImage);
        }
        
        if (modelFile) {
            modelFileUrl = await uploadFile(modelFile);
        }

        // Upload New Gallery Images
        if (galleryFiles.length > 0) {
             const uploadPromises = galleryFiles.map(file => uploadFile(file));
             newGalleryUrls = await Promise.all(uploadPromises);
        }

        // Combine existing retained images + new ones
        const finalImages = [...bookState.images, ...newGalleryUrls];

        const productData = {
            title: bookState.title,
            additionalDetails: { author: bookState.author },
            currentPrice: bookState.currentPrice,
            previousPrice: bookState.previousPrice,
            description: bookState.description,
            coverImage: coverImageUrl,
            images: finalImages
        };

        if (modelFileUrl) {
            productData.modelUrl = modelFileUrl;
        }

        console.log("EditProduct Payload:", productData);

        const res = await fetch(`${productsUrl}${id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(productData)
        });

        if (res.ok) {
            toast.success("Product Updated Successfully!");
            navigate("/products");
        } else {
            toast.error("Failed to update product");
        }

    } catch (error) {
        console.error(error);
        toast.error("Error updating product");
    } finally {
        setLoading(false);
    }
  };

  if (fetching) return <div className="p-4">Loading product details...</div>;

  return (
    <div className="content-wrapper">
        <h2 className="mb-4">Edit Product</h2>
        <div className="row">
            <div className="col-md-8">
                <form onSubmit={handleSubmit} className="container bg-white p-4 rounded shadow">
                    <div className="mb-3">
                        <label className="form-label">Title</label>
                        <input name="title" value={bookState.title} onChange={handleChange} className="form-control" required />
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Designer / Author</label>
                        <input name="author" value={bookState.author} onChange={handleChange} className="form-control" />
                    </div>

                    <div className="row mb-3">
                        <div className="col">
                            <label className="form-label">Current Price</label>
                            <input name="currentPrice" type="number" value={bookState.currentPrice} onChange={handleChange} className="form-control" required />
                        </div>
                        <div className="col">
                            <label className="form-label">Previous Price</label>
                            <input name="previousPrice" type="number" value={bookState.previousPrice} onChange={handleChange} className="form-control" />
                        </div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Description</label>
                        <textarea name="description" value={bookState.description} onChange={handleChange} className="form-control" rows="3"></textarea>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Replace 3D Model (Optional)</label>
                        <input type="file" className="form-control" onChange={handleModelSelect} accept=".glb,.gltf,.stl" />
                        <div className="form-text">Upload to replace existing model.</div>
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Cover Image</label>
                        <input type="file" className="form-control" onChange={(e) => setCoverImage(e.target.files[0])} accept="image/*" />
                        <div className="form-text">Upload manually or generate from 3D preview.</div>
                        
                        {/* Show generated preview if available, else show existing image */}
                        {generatedThumbnail ? (
                            <img src={generatedThumbnail} alt="New Thumbnail" className="mt-2 img-thumbnail" style={{maxHeight: '100px'}} />
                        ) : existingCoverUrl && (
                             <div className="mt-2">
                                <p className="mb-1 text-muted small">Current Image:</p>
                                <img src={existingCoverUrl} alt="Current" className="img-thumbnail" style={{maxHeight: '100px'}} />
                             </div>
                        )}
                    </div>

                    <div className="mb-3">
                        <label className="form-label">Product Gallery</label>
                        <input 
                            type="file" 
                            className="form-control" 
                            onChange={(e) => setGalleryFiles(Array.from(e.target.files))} 
                            accept="image/*" 
                            multiple 
                        />
                        <div className="form-text">Upload additional images for the carousel.</div>
                        
                        {/* New files preview */}
                        {galleryFiles.length > 0 && (
                            <div className="mt-2 d-flex gap-2 flex-wrap mb-2">
                                {galleryFiles.map((file, i) => (
                                    <span key={i} className="badge bg-info">{file.name}</span>
                                ))}
                            </div>
                        )}

                        {/* Existing Gallery Images */}
                        {bookState.images && bookState.images.length > 0 && (
                            <div className="mt-3">
                                <label className="form-label small">Current Gallery:</label>
                                <div className="d-flex gap-2 flex-wrap">
                                    {bookState.images.map((img, i) => (
                                        <div key={i} className="position-relative">
                                            <img 
                                                src={img.startsWith('http') ? img : `https://backend.cdp3d.com${img}`} 
                                                alt={`Gallery ${i}`} 
                                                className="img-thumbnail" 
                                                style={{width: '80px', height: '80px', objectFit: 'cover'}} 
                                            />
                                            <button 
                                                type="button"
                                                className="btn btn-danger btn-sm position-absolute top-0 end-0 p-0"
                                                style={{width: '20px', height: '20px', lineHeight: '18px', fontSize: '12px'}}
                                                onClick={() => handleRemoveImage(i)}
                                            >
                                                &times;
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? "Updating..." : "Update Product"}
                    </button>
                    <button type="button" className="btn btn-secondary ms-2" onClick={() => navigate('/products')}>
                        Cancel
                    </button>
                </form>
            </div>
            
            <div className="col-md-4">
                {modelPreviewUrl && (
                    <div className="card shadow sticky-top" style={{top: '20px'}}>
                        <div className="card-header bg-dark text-white">3D Preview (New Model)</div>
                        <div className="card-body p-0" style={{height: '300px'}}>
                            <Canvas gl={{ preserveDrawingBuffer: true }} camera={{ fov: 50 }}>
                                <Suspense fallback={<Html center>Loading...</Html>}>
                                    <Stage environment="city" intensity={0.6}>
                                        <Model url={modelPreviewUrl} fileName={modelFileName} />
                                    </Stage>
                                </Suspense>
                                <OrbitControls autoRotate />
                            </Canvas>
                        </div>
                        <div className="card-footer text-center">
                            <button type="button" className="btn btn-outline-secondary btn-sm" onClick={handleCaptureThumbnail}>
                                <i className="bi bi-camera me-1"></i> Capture Thumbnail
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    </div>
  );
};

export default EditProduct;
